import React from 'react';
import { Row, Col, Typography, Card, Avatar, Progress, Badge, Button } from 'antd';
import styled from 'styled-components';
import { 
  TrophyOutlined, 
  EyeOutlined, 
  HeartOutlined, 
  MessageOutlined,
  UserAddOutlined,
  BellOutlined,
  CalendarOutlined,
  DollarOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

// Mock data for demo
const mockDashboardData = {
  stats: {
    profileViews: 1247,
    connections: 89,
    projectsCompleted: 12,
    earnings: 2450
  },
  recentActivity: [
    {
      type: 'view',
      message: 'Nike viewed your profile',
      time: '2 hours ago',
      icon: <EyeOutlined />
    },
    {
      type: 'connection',
      message: 'Sarah Chen wants to connect',
      time: '4 hours ago',
      icon: <UserAddOutlined />
    },
    {
      type: 'project',
      message: 'Project "Summer Campaign" completed',
      time: '1 day ago',
      icon: <TrophyOutlined />
    }
  ],
  opportunities: [
    {
      brand: 'Adidas',
      title: 'Social Media Content Creator',
      budget: '$500 - $1,000',
      deadline: '5 days left',
      match: 95
    },
    {
      brand: 'Spotify',
      title: 'Music Video Producer',
      budget: '$1,500 - $3,000',
      deadline: '2 weeks left',
      match: 87
    },
    {
      brand: 'Local Coffee Co.',
      title: 'Brand Photography',
      budget: '$300 - $600',
      deadline: '1 week left',
      match: 92
    }
  ],
  skillProgress: [
    { skill: 'Video Editing', progress: 85 },
    { skill: 'Photography', progress: 92 },
    { skill: 'Social Media', progress: 78 },
    { skill: 'Content Strategy', progress: 70 }
  ]
};

// Styled Components
const DashboardContainer = styled.div`
  background: ${props => props.theme.colors?.neutral?.[950] || '#0a0a0a'};
  min-height: 600px;
  padding: ${props => props.theme.spacing?.[6] || '1.5rem'};
`;

const WelcomeSection = styled.div`
  margin-bottom: ${props => props.theme.spacing?.[6] || '1.5rem'};
`;

const WelcomeTitle = styled(Title)`
  && {
    color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
    margin-bottom: ${props => props.theme.spacing?.[2] || '0.5rem'};
    font-family: ${props => props.theme.typography?.fontFamily?.heading || "'Hanson Bold', 'Inter', system-ui, sans-serif"};
  }
`;

const WelcomeText = styled(Text)`
  && {
    color: ${props => props.theme.colors?.neutral?.[300] || '#d4d4d4'};
    font-size: ${props => props.theme.typography?.fontSize?.base || '1rem'};
  }
`;

const StatsCard = styled(Card)`
  && {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: ${props => props.theme.borderRadius?.lg || '0.5rem'};
    text-align: center;
    transition: all ${props => props.theme.animation?.duration?.normal || '300ms'};
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${props => props.theme.boxShadow?.lg || '0 10px 15px -3px rgba(0, 0, 0, 0.1)'};
    }
    
    .ant-card-body {
      background: transparent;
      padding: ${props => props.theme.spacing?.[4] || '1rem'};
    }
  }
`;

const StatNumber = styled(Title)`
  && {
    color: ${props => props.theme.colors?.secondary?.[400] || '#fb923c'};
    margin-bottom: ${props => props.theme.spacing?.[1] || '0.25rem'};
    font-size: ${props => props.theme.typography?.fontSize?.['2xl'] || '1.5rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.bold || '700'};
  }
`;

const StatLabel = styled(Text)`
  && {
    color: ${props => props.theme.colors?.neutral?.[300] || '#d4d4d4'};
    font-size: ${props => props.theme.typography?.fontSize?.sm || '0.875rem'};
  }
`;

const ActivityCard = styled(Card)`
  && {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: ${props => props.theme.borderRadius?.lg || '0.5rem'};
    margin-bottom: ${props => props.theme.spacing?.[4] || '1rem'};
    
    .ant-card-head {
      background: transparent;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      
      .ant-card-head-title {
        color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
        font-weight: ${props => props.theme.typography?.fontWeight?.bold || '700'};
      }
    }
    
    .ant-card-body {
      background: transparent;
      max-height: 300px;
      overflow-y: auto;
    }
  }
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => props.theme.spacing?.[3] || '0.75rem'} 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius?.full || '9999px'};
  background: ${props => props.theme.gradients?.primary || 'linear-gradient(135deg, #e22784 0%, #fc673d 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${props => props.theme.spacing?.[3] || '0.75rem'};
  
  .anticon {
    color: white;
    font-size: 1rem;
  }
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityMessage = styled(Text)`
  && {
    color: ${props => props.theme.colors?.neutral?.[200] || '#e5e5e5'};
    display: block;
    margin-bottom: ${props => props.theme.spacing?.[1] || '0.25rem'};
  }
`;

const ActivityTime = styled(Text)`
  && {
    color: ${props => props.theme.colors?.neutral?.[400] || '#a3a3a3'};
    font-size: ${props => props.theme.typography?.fontSize?.xs || '0.75rem'};
  }
`;

const OpportunityCard = styled(Card)`
  && {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: ${props => props.theme.borderRadius?.lg || '0.5rem'};
    margin-bottom: ${props => props.theme.spacing?.[3] || '0.75rem'};
    transition: all ${props => props.theme.animation?.duration?.normal || '300ms'};
    
    &:hover {
      transform: translateY(-2px);
      border-color: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
    }
    
    .ant-card-body {
      background: transparent;
      padding: ${props => props.theme.spacing?.[4] || '1rem'};
    }
  }
`;

const OpportunityBrand = styled(Text)`
  && {
    color: ${props => props.theme.colors?.secondary?.[400] || '#fb923c'};
    font-weight: ${props => props.theme.typography?.fontWeight?.semibold || '600'};
    display: block;
  }
`;

const OpportunityTitle = styled(Text)`
  && {
    color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
    font-weight: ${props => props.theme.typography?.fontWeight?.medium || '500'};
    display: block;
    margin-bottom: ${props => props.theme.spacing?.[2] || '0.5rem'};
  }
`;

const OpportunityDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing?.[2] || '0.5rem'};
`;

const OpportunityBudget = styled(Text)`
  && {
    color: ${props => props.theme.colors?.neutral?.[300] || '#d4d4d4'};
    font-size: ${props => props.theme.typography?.fontSize?.sm || '0.875rem'};
  }
`;

const OpportunityDeadline = styled(Text)`
  && {
    color: ${props => props.theme.colors?.neutral?.[400] || '#a3a3a3'};
    font-size: ${props => props.theme.typography?.fontSize?.xs || '0.75rem'};
  }
`;

const MatchBadge = styled(Badge)`
  && {
    .ant-badge-count {
      background: ${props => props.theme.gradients?.primary || 'linear-gradient(135deg, #e22784 0%, #fc673d 100%)'};
      border: none;
      font-weight: ${props => props.theme.typography?.fontWeight?.semibold || '600'};
    }
  }
`;

const SkillProgressItem = styled.div`
  margin-bottom: ${props => props.theme.spacing?.[4] || '1rem'};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SkillName = styled(Text)`
  && {
    color: ${props => props.theme.colors?.neutral?.[200] || '#e5e5e5'};
    display: block;
    margin-bottom: ${props => props.theme.spacing?.[2] || '0.5rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.medium || '500'};
  }
`;

const DashboardDemo = () => {
  return (
    <DashboardContainer>
      <WelcomeSection>
        <WelcomeTitle level={3}>Welcome back, Alex! 👋</WelcomeTitle>
        <WelcomeText>Here's what's happening with your creative journey today.</WelcomeText>
      </WelcomeSection>

      <Row gutter={[16, 16]}>
        {/* Stats Section */}
        <Col xs={24} sm={12} lg={6}>
          <StatsCard>
            <StatNumber level={4}>{mockDashboardData.stats.profileViews.toLocaleString()}</StatNumber>
            <StatLabel>Profile Views</StatLabel>
          </StatsCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard>
            <StatNumber level={4}>{mockDashboardData.stats.connections}</StatNumber>
            <StatLabel>Connections</StatLabel>
          </StatsCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard>
            <StatNumber level={4}>{mockDashboardData.stats.projectsCompleted}</StatNumber>
            <StatLabel>Projects</StatLabel>
          </StatsCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatsCard>
            <StatNumber level={4}>${mockDashboardData.stats.earnings.toLocaleString()}</StatNumber>
            <StatLabel>Earnings</StatLabel>
          </StatsCard>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '1.5rem' }}>
        {/* Recent Activity */}
        <Col xs={24} lg={12}>
          <ActivityCard title="Recent Activity" size="small">
            {mockDashboardData.recentActivity.map((activity, index) => (
              <ActivityItem key={index}>
                <ActivityIcon>
                  {activity.icon}
                </ActivityIcon>
                <ActivityContent>
                  <ActivityMessage>{activity.message}</ActivityMessage>
                  <ActivityTime>{activity.time}</ActivityTime>
                </ActivityContent>
              </ActivityItem>
            ))}
          </ActivityCard>
        </Col>

        {/* Skill Progress */}
        <Col xs={24} lg={12}>
          <ActivityCard title="Skill Progress" size="small">
            {mockDashboardData.skillProgress.map((skill, index) => (
              <SkillProgressItem key={index}>
                <SkillName>{skill.skill}</SkillName>
                <Progress 
                  percent={skill.progress} 
                  strokeColor={{
                    '0%': '#e22784',
                    '100%': '#fc673d',
                  }}
                  trailColor="rgba(255, 255, 255, 0.1)"
                  size="small"
                />
              </SkillProgressItem>
            ))}
          </ActivityCard>
        </Col>
      </Row>

      {/* Opportunities */}
      <Row style={{ marginTop: '1.5rem' }}>
        <Col span={24}>
          <ActivityCard title="Brand Opportunities" size="small">
            <Row gutter={[16, 16]}>
              {mockDashboardData.opportunities.map((opportunity, index) => (
                <Col xs={24} md={12} lg={8} key={index}>
                  <OpportunityCard>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                      <div style={{ flex: 1 }}>
                        <OpportunityBrand>{opportunity.brand}</OpportunityBrand>
                        <OpportunityTitle>{opportunity.title}</OpportunityTitle>
                      </div>
                      <MatchBadge count={`${opportunity.match}% match`} />
                    </div>
                    <OpportunityDetails>
                      <OpportunityBudget>{opportunity.budget}</OpportunityBudget>
                      <OpportunityDeadline>{opportunity.deadline}</OpportunityDeadline>
                    </OpportunityDetails>
                    <Button 
                      type="primary" 
                      size="small" 
                      style={{ 
                        background: 'linear-gradient(135deg, #e22784 0%, #fc673d 100%)',
                        border: 'none',
                        width: '100%'
                      }}
                    >
                      Apply Now
                    </Button>
                  </OpportunityCard>
                </Col>
              ))}
            </Row>
          </ActivityCard>
        </Col>
      </Row>
    </DashboardContainer>
  );
};

export default DashboardDemo;
